import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Save, Paperclip, RefreshCw, Trash2, Play, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { playNotificationSound } from "../lib/notificationSound";

const EVENT_LABEL = {
  NEW: "طلب جديد",
  PREPARING: "جاري التجهيز",
  READY: "جاهز",
  OUT_FOR_DELIVERY: "خرج للتوصيل",
  CUSTOMER_ARRIVED: "العميل وصل",
  COMPLETED: "تم التسليم",
  CANCELLED: "ملغي",
};
const RECIPIENT_LABEL = { CUSTOMER: "العميل", RESTAURANT: "المطعم" };

export default function AdminNotifications() {
  const nav = useNavigate();
  const [rules, setRules] = useState([]);
  const [sounds, setSounds] = useState([""]);
  const [customSounds, setCustomSounds] = useState([]);
  const [busyKey, setBusyKey] = useState("");
  const fileRefs = useRef({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/notification-rules")
      .then(({ data }) => {
        setRules(data.rules);
        setSounds(data.sounds || [""]);
        setCustomSounds(data.custom_sounds || []);
      })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  }, []);

  const soundLabel = (s) => {
    if (!s) return "بدون صوت";
    const c = customSounds.find((x) => x.sound === s);
    return c ? c.label : s;
  };

  const uploadSound = async (key, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("حجم الملف يجب أن يكون أقل من 2 ميجابايت"); return; }
    setBusyKey(key);
    const form = new FormData();
    form.append("file", file);
    form.append("rule_key", key);
    try {
      const { data } = await api.post("/admin/sounds", form);
      setCustomSounds(data.custom_sounds || []);
      patch(key, "sound", data.sound);
      toast.success("تم إرفاق الصوت");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusyKey(""); }
  };

  const restoreDefault = async (key) => {
    if (!window.confirm("سيتم استعادة الصوت الافتراضي لهذه القاعدة. متابعة؟")) return;
    setBusyKey(key);
    try {
      const { data } = await api.post(`/admin/notification-rules/${key}/sound/restore-default`);
      setCustomSounds(data.custom_sounds || []);
      patch(key, "sound", data.sound);
      toast.success("تم استعادة الصوت الافتراضي");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusyKey(""); }
  };

  const deleteSound = async (key) => {
    if (!window.confirm("سيتم إلغاء الصوت لهذه القاعدة. متابعة؟")) return;
    setBusyKey(key);
    try {
      const { data } = await api.delete(`/admin/notification-rules/${key}/sound`);
      setCustomSounds(data.custom_sounds || []);
      patch(key, "sound", "");
      toast.success("تم حذف الصوت");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusyKey(""); }
  };

  const patch = (key, field, value) =>
    setRules((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/notification-rules", { rules });
      setRules(data.rules);
      toast.success("تم حفظ إعدادات الإشعارات");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="bg-white border-b border-orange-100">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/admin")} className="pill bg-white border border-orange-100 px-3 py-2" data-testid="notif-back-btn">
              <ArrowRight size={16} />
            </button>
            <div>
              <div className="text-xs text-gray-500">لوحة المشرف</div>
              <div className="font-extrabold">الإشعارات</div>
            </div>
          </div>
          <button onClick={save} disabled={saving || loading}
            className="pill brand-grad text-white px-4 py-2.5 font-bold text-sm inline-flex items-center gap-1 disabled:opacity-60"
            data-testid="save-notification-rules">
            <Save size={16} /> حفظ
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-5">
        <h1 className="text-2xl font-extrabold mb-1">قواعد إشعارات الطلبات</h1>
        <div className="text-sm text-gray-600 mb-4">قواعد عامة للنظام بالكامل. تُستخدم عند كل انتقال حقيقي لحالة الطلب.</div>

        {loading && <div className="text-gray-500">جاري التحميل…</div>}

        <div className="space-y-3" data-testid="notification-rules-list">
          {rules.map((r) => (
            <div key={r.key} className="bg-white rounded-2xl border border-orange-100 p-4" data-testid={`rule-${r.key}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="pill bg-orange-100 text-orange-800 text-xs font-bold px-2.5 py-1">{EVENT_LABEL[r.event] || r.event}</span>
                  <span className="pill bg-gray-100 text-gray-700 text-xs font-bold px-2.5 py-1">{RECIPIENT_LABEL[r.recipient]}</span>
                </div>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-600">
                  <input type="checkbox" checked={r.enabled} onChange={(e) => patch(r.key, "enabled", e.target.checked)}
                    className="w-4 h-4 accent-orange-600" data-testid={`rule-enabled-${r.key}`} />
                  مفعّل
                </label>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-500 mb-1">عنوان الإشعار</div>
                  <input value={r.title} onChange={(e) => patch(r.key, "title", e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" data-testid={`rule-title-${r.key}`} />
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">نص الإشعار (استخدم ORDER_NUMBER لرقم الطلب)</div>
                  <input value={r.body} onChange={(e) => patch(r.key, "body", e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" data-testid={`rule-body-${r.key}`} />
                </div>
                {r.recipient === "RESTAURANT" && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">الصوت داخل لوحة المطعم</div>
                    <select value={r.sound} onChange={(e) => patch(r.key, "sound", e.target.value)}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white" data-testid={`rule-sound-${r.key}`}>
                      {[...new Set([...sounds, ...customSounds.map((c) => c.sound), r.sound])].map((s) => (
                        <option key={s || "none"} value={s}>{soundLabel(s)}</option>
                      ))}
                    </select>
                    <input type="file" accept=".mp3,.wav,.ogg,audio/*" className="hidden"
                      ref={(el) => { fileRefs.current[r.key] = el; }}
                      onChange={(e) => { uploadSound(r.key, e.target.files?.[0]); e.target.value = ""; }}
                      data-testid={`rule-sound-file-${r.key}`} />
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <button type="button" disabled={busyKey === r.key}
                        onClick={() => fileRefs.current[r.key]?.click()}
                        className="pill bg-white border border-orange-200 text-orange-700 text-xs font-bold px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
                        data-testid={r.sound ? `rule-sound-change-${r.key}` : `rule-sound-attach-${r.key}`}>
                        {r.sound ? <RefreshCw size={13} /> : <Paperclip size={13} />}
                        {r.sound ? "تغيير" : "إرفاق"}
                      </button>
                      {r.sound && (
                        <>
                          <button type="button" onClick={() => playNotificationSound(r.sound)}
                            className="pill bg-white border border-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 inline-flex items-center gap-1"
                            data-testid={`rule-sound-play-${r.key}`}>
                            <Play size={13} /> تشغيل
                          </button>
                          <button type="button" disabled={busyKey === r.key} onClick={() => deleteSound(r.key)}
                            className="pill bg-white border border-red-200 text-red-600 text-xs font-bold px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
                            data-testid={`rule-sound-delete-${r.key}`}>
                            <Trash2 size={13} /> حذف
                          </button>
                        </>
                      )}
                      {r.default_sound && (
                        <button type="button" disabled={busyKey === r.key} onClick={() => restoreDefault(r.key)}
                          className="pill bg-white border border-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
                          data-testid={`rule-sound-restore-${r.key}`}>
                          <RotateCcw size={13} /> استعادة الافتراضي
                        </button>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1.5">MP3 / WAV / OGG — الحد الأقصى 2 ميجابايت</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
